/**
 * @fileoverview `<PhoneNumberInput>` — saisie d'un numéro de téléphone
 * Côte d'Ivoire, préfixe `+225` en dur avec drapeau CI.
 *
 * L'utilisateur ne peut PAS modifier le préfixe : il est affiché comme
 * un élément visuel non-éditable à gauche du champ. La zone saisissable
 * n'accepte que les 10 chiffres locaux, formatés à la volée en
 * `07 XX XX XX XX` pour la lisibilité.
 *
 * La valeur exposée par `onChange` est toujours au format **E.164 canonique**
 * (`+2250700000000`, 13 caractères, sans espaces) attendu par l'API GNPC
 * (`POST /auth/otp/request`, spec §OTP). Si la saisie est incomplète, la
 * valeur exposée est la chaîne vide `""` — le composant ne renvoie jamais
 * un numéro à moitié.
 *
 * Non contrôlable : ce composant n'accepte pas d'autres pays. La CI est
 * la seule zone couverte par GNPC (portail citoyen limité aux résidents
 * ivoiriens). Un composant multi-pays viendrait à l'ajout d'un second
 * marché — pas dans le périmètre v1.
 */

import { forwardRef, useId, useMemo } from 'react';
import { cn } from '../lib/utils.ts';
import { CoteDivoireFlag } from './flags/cote-divoire-flag.tsx';

/**
 * Taille du champ — cohérente avec `<Input>` (Guide frontend §6.2 : ≥ 44 px).
 */
type PhoneNumberInputSize = 'sm' | 'md' | 'lg';

export interface PhoneNumberInputProps {
  /**
   * Numéro en E.164 canonique (`+2250700000000`) ou chaîne vide. Le
   * composant extrait automatiquement les 10 chiffres locaux et formate
   * l'affichage. Toute valeur non-CI est ignorée (le préfixe reste `+225`).
   */
  value?: string;
  /**
   * Reçoit la valeur en E.164 canonique dès qu'elle atteint 10 chiffres,
   * ou `""` tant que la saisie est incomplète. Ne se déclenche jamais avec
   * une valeur partielle — évite d'envoyer un numéro invalide à l'API.
   */
  onChange?: (e164: string) => void;
  /** Handler `onBlur` transmis à l'`<input>` interne. */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * Placeholder visible du champ. Défaut : `07 00 00 00 00`. Ne pas mettre
   * `+225` — il est déjà affiché à gauche.
   */
  placeholder?: string;
  /** Alias de taille — défaut `md` (44 px, cible tactile GNPC). */
  size?: PhoneNumberInputSize;
  disabled?: boolean;
  readOnly?: boolean;
  /** Marque le champ en erreur (bordure danger + `aria-invalid`). */
  'aria-invalid'?: boolean | 'true' | 'false';
  /** Passé à l'input interne — utile pour lier avec un `<label htmlFor>`. */
  id?: string;
  /** ID d'aide utilisé pour l'accessibilité (hint / erreur). */
  'aria-describedby'?: string;
  /** Autocomplete HTML — défaut `tel-national`. */
  autoComplete?: string;
  name?: string;
  className?: string;
}

const SIZES: Record<PhoneNumberInputSize, string> = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

/**
 * Retire tout ce qui n'est pas un chiffre, coupe à 10 chars max.
 */
function toDigits(input: string): string {
  return input.replace(/\D+/g, '').slice(0, 10);
}

/**
 * Formatte les chiffres locaux en `07 XX XX XX XX` (groupes de 2).
 */
function formatDisplay(digits: string): string {
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

/**
 * Extrait les 10 chiffres locaux depuis une valeur en E.164 CI ou
 * chaîne libre. Retourne `""` si aucun chiffre exploitable.
 */
function extractLocalDigits(value: string | undefined): string {
  if (!value) return '';
  const withoutPrefix = value.replace(/^\+?225/, '');
  return toDigits(withoutPrefix);
}

export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  function PhoneNumberInput(
    {
      value,
      onChange,
      onBlur,
      placeholder = '07 00 00 00 00',
      size = 'md',
      disabled = false,
      readOnly = false,
      className,
      autoComplete = 'tel-national',
      name,
      id: providedId,
      ...ariaProps
    },
    ref,
  ): React.JSX.Element {
    const reactId = useId();
    const inputId = providedId ?? reactId;
    const invalid = ariaProps['aria-invalid'] === true || ariaProps['aria-invalid'] === 'true';

    // Affichage dérivé de la value contrôlée. Si `value` est absente,
    // l'input est non-contrôlé (utile en formulaires simples).
    const controlledDisplay = useMemo(() => {
      if (value === undefined) return undefined;
      return formatDisplay(extractLocalDigits(value));
    }, [value]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = toDigits(event.target.value);
      // Ré-affiche formaté (l'input est contrôlé si value fourni).
      event.target.value = formatDisplay(digits);
      // On ne notifie l'appelant qu'avec un numéro complet ; sinon vide.
      const e164 = digits.length === 10 ? `+225${digits}` : '';
      onChange?.(e164);
    };

    return (
      <div
        className={cn(
          'group flex w-full min-w-0 items-stretch overflow-hidden rounded-md border border-input bg-background text-foreground',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          invalid && 'border-danger ring-danger/20 focus-within:border-danger focus-within:ring-danger/20',
          disabled && 'pointer-events-none cursor-not-allowed opacity-50',
          SIZES[size],
          className,
        )}
      >
        {/* Préfixe fixe — non éditable, non focusable. Le drapeau reste
          * décoratif (aria-hidden). Séparateur vertical pour matérialiser
          * la coupure entre préfixe et zone de saisie. */}
        <span className="flex select-none items-center gap-2 border-r border-input bg-muted px-3 text-muted-foreground">
          {/* `border-black/10` en dur — pas de `ring-*` (Tailwind traduit les
            * rings en box-shadow, or les ombres sont bannies du DS GNPC). */}
          <CoteDivoireFlag className="h-3.5 w-5 rounded-[1px] border border-black/10" />
          <span className="font-medium tabular-nums text-foreground">+225</span>
        </span>
        <input
          ref={ref}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          data-slot="phone-number-input"
          id={inputId}
          name={name}
          value={controlledDisplay}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={ariaProps['aria-invalid']}
          aria-describedby={ariaProps['aria-describedby']}
          className={cn(
            'w-full min-w-0 flex-1 bg-transparent px-3 tabular-nums outline-none',
            'placeholder:text-muted-foreground',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
      </div>
    );
  },
);
